import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const money = (cur, v) => (cur === "NGN" ? `₦${Number(v || 0).toLocaleString("en-NG")}` : `US$${Number(v || 0).toLocaleString()}`);

function FindLink() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const send = async () => {
    try { const { data } = await api.post("/sponsorships/resend-link", { email }); setMsg(data.message); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="max-w-md mx-auto" data-testid="sponsorship-find-link">
      <h1 className="font-display font-bold text-2xl text-emerald-950">Manage your sponsorship</h1>
      <p className="mt-2 text-sm text-gray-600">Enter the email you used when sponsoring. We'll send you a private link to view payments, update your card or cancel.</p>
      <div className="mt-5 flex gap-2">
        <Input data-testid="sponsorship-find-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button data-testid="sponsorship-find-submit" onClick={send} disabled={!email} className="bg-emerald-700 hover:bg-emerald-800">Send link</Button>
      </div>
      {msg && <p className="mt-3 text-sm text-emerald-800" data-testid="sponsorship-find-message">{msg}</p>}
    </div>
  );
}

const Stat = ({ k, v, id }) => (
  <div className="rounded-2xl border border-emerald-100 bg-white p-4"><div className="text-xs text-gray-500">{k}</div><div className="font-display font-bold text-lg text-emerald-950" data-testid={id}>{v}</div></div>
);

function Details({ token }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/sponsorships/manage/${token}`).then(({ data }) => setS(data.sponsorship)).catch((e) => setErr(formatApiError(e))); }, [token]);
  useEffect(() => { load(); }, [load]);
  const act = async (path) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sponsorships/manage/${token}/${path}`);
      if (data.url) window.location.href = data.url; else { toast.success("Monthly sponsorship cancelled. No further charges will be made."); load(); }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (err) return <p className="text-center text-red-600" data-testid="sponsorship-manage-error">{err}</p>;
  if (!s) return <Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-600" />;
  const monthlyActive = s.frequency === "monthly" && s.status === "active";
  return (
    <div data-testid="sponsorship-manage">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display font-bold text-2xl text-emerald-950">Your family sponsorship</h1>
        <Badge className="bg-emerald-100 text-emerald-800" data-testid="sponsorship-status">{s.status}</Badge>
      </div>
      <p className="mt-1 text-sm text-gray-500 font-mono">{s.ref} · {s.purpose_label}</p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat k="Pledge" v={`US$${s.amount_usd}${s.frequency === "monthly" ? "/month" : ""}`} id="sponsorship-pledge" />
        <Stat k="Period" v={s.frequency === "monthly" ? (s.duration_months ? `${s.duration_months} months` : "Ongoing") : "One-time"} id="sponsorship-period" />
        <Stat k="Total contributed" v={money(s.currency, s.total_received)} id="sponsorship-total" />
        <Stat k="Payments made" v={s.payments.length} id="sponsorship-payment-count" />
      </div>
      <h2 className="mt-8 font-display font-semibold text-emerald-950">Payment history & receipts</h2>
      <div className="mt-2 rounded-2xl border border-emerald-100 bg-white divide-y">
        {s.payments.length === 0 && <p className="p-4 text-sm text-gray-500">No confirmed payments yet.</p>}
        {s.payments.map((p) => (
          <div key={p.reference} className="p-4 flex justify-between text-sm" data-testid={`sponsorship-payment-${p.reference}`}>
            <span><span className="font-mono text-xs">{p.reference}</span><span className="block text-gray-500 text-xs">{(p.paid_at || "").slice(0, 10)}</span></span>
            <span className="text-right font-semibold">{money(p.currency, p.amount)}<span className="block text-xs font-normal text-gray-500">{p.status}</span></span>
          </div>
        ))}
      </div>
      <h2 className="mt-8 font-display font-semibold text-emerald-950">Family support allocation</h2>
      <div className="mt-2 rounded-2xl border border-emerald-100 bg-white p-4 text-sm" data-testid="sponsorship-allocations">
        {s.allocations.length === 0
          ? <p className="text-gray-600">Awaiting allocation. TMF assigns support to eligible, verified families from the sponsorship pool. Family details stay private to protect beneficiaries.</p>
          : s.allocations.map((a, i) => <p key={i} className="py-1">{money(a.currency, a.amount)} allocated to <b>{a.programme_name}</b> on {(a.allocated_at || "").slice(0, 10)}</p>)}
      </div>
      {monthlyActive && (
        <div className="mt-8 flex flex-wrap gap-3">
          <Button variant="outline" disabled={busy} onClick={() => act("update-card")} className="rounded-full" data-testid="sponsorship-update-card-btn">Update payment details</Button>
          <Button variant="outline" disabled={busy} onClick={() => window.confirm("Cancel your monthly sponsorship? No further charges will be made.") && act("cancel")} className="rounded-full border-red-200 text-red-700" data-testid="sponsorship-cancel-btn">Cancel monthly sponsorship</Button>
        </div>
      )}
    </div>
  );
}

export default function SponsorshipManage() {
  const { token } = useParams();
  return (
    <div>
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-16">{token ? <Details token={token} /> : <FindLink />}</main>
      <Footer />
    </div>
  );
}
